import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { investorAPI, fileAPI, getApiUrl } from '../lib/api';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import FileTraceModal from '../components/FileTraceModal';
import StartupPortfolioCard from '../components/StartupPortfolioCard';
import ProgressBar from '../components/ProgressBar';

const VALID_TABS = ['portfolio', 'documents', 'events'];

const InvestorDashboard = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const pathTab = location.pathname.split('/').pop(); // 'dashboard' | 'documents' | 'events'
	const activeTab = VALID_TABS.includes(pathTab) ? pathTab : 'portfolio';

	const setActiveTab = (tab) => {
		const tenant = user?.tenantIdentifier || 'default';
		if (tab === 'portfolio') navigate(`/${tenant}/investor/dashboard`);
		else navigate(`/${tenant}/investor/${tab}`);
	};

	// Normalize invalid URL tabs (e.g. /investor/dashboard/undefined)
	useEffect(() => {
		if (pathTab && pathTab !== 'dashboard' && !VALID_TABS.includes(pathTab)) {
			const tenant = user?.tenantIdentifier || 'default';
			navigate(`/${tenant}/investor/dashboard`, { replace: true });
		}
	}, [pathTab, user?.tenantIdentifier, navigate]);
	const [files, setFiles] = useState([]);
	const [fileTab, setFileTab] = useState('received');
	const [documentsPage, setDocumentsPage] = useState(1);
	const [documentsPageSize, setDocumentsPageSize] = useState(10);
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedFile, setSelectedFile] = useState(null);
	const [showFileModal, setShowFileModal] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [selectedUploadFile, setSelectedUploadFile] = useState(null);
	const [tags, setTags] = useState([]);
	const [newTag, setNewTag] = useState('');
	const [uploadProgress, setUploadProgress] = useState(0);
	const [dragActive, setDragActive] = useState(false);
	const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
	const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning', confirmText: 'Confirm' });
	const [startups, setStartups] = useState([]);
	const [investments, setInvestments] = useState([]);
	const [events, setEvents] = useState([]);
	const [loadingStartups, setLoadingStartups] = useState(false);
	const [loadingEvents, setLoadingEvents] = useState(false);
	const [showProfileMenu, setShowProfileMenu] = useState(false);
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [showEventModal, setShowEventModal] = useState(false);
	const [traceFile, setTraceFile] = useState(null);
	const [showTraceModal, setShowTraceModal] = useState(false);
	const profileMenuRef = useRef(null);
	const [linkedNetwork, setLinkedNetwork] = useState(null);
	const [showAddInvestmentModal, setShowAddInvestmentModal] = useState(false);
	const [addInvestmentSubmitting, setAddInvestmentSubmitting] = useState(false);
	const [addInvestmentForm, setAddInvestmentForm] = useState({
		startupId: '',
		investmentAmount: '',
		investmentStage: 'SEED',
		investmentType: 'EQUITY',
		currency: 'INR',
		investmentDate: new Date().toISOString().split('T')[0],
		notes: ''
	});

	// Fetch /users/me for investorNetworkId and linked network details (so Network option and linked network card show)
	useEffect(() => {
		if (user?.role !== 'INVESTOR') return;
		const token = localStorage.getItem('token');
		if (!token) return;
		fetch(getApiUrl('users/me'), { headers: { Authorization: `Bearer ${token}` } })
			.then((res) => (res.ok ? res.json() : null))
			.then((data) => {
				if (data?.investorNetworkId != null) {
					setLinkedNetwork({
						id: data.investorNetworkId,
						name: data.investorNetworkName || data.investorNetworkOrganizationName || 'Network',
						organizationName: data.investorNetworkOrganizationName || data.investorNetworkName
					});
				}
			})
			.catch(() => {});
	}, [user?.role]);

	const effectiveInvestorNetworkId = user?.investorNetworkId ?? linkedNetwork?.id;

	// Close profile menu when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
				setShowProfileMenu(false);
			}
		};

		if (showProfileMenu) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showProfileMenu]);

	useEffect(() => {
		if (user?.id) {
			loadFiles();
			loadPortfolio();
			loadEvents();
		}
	}, [user?.id]);

	const loadFiles = async () => {
		setLoading(true);
		try {
			const [sharedFiles, myFiles] = await Promise.all([
				investorAPI.getSharedFiles(),
				fileAPI.getMyFiles()
			]);

			// Merge and deduplicate files based on ID
			const allFiles = [...sharedFiles, ...myFiles];
			const uniqueFiles = Array.from(new Map(allFiles.map(file => [file.id, file])).values());

			setFiles(uniqueFiles.map(file => ({ ...file, tags: file.tags || [] })));
		} catch (err) {
			console.error('Failed to load files:', err);
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to load files', type: 'error' });
		} finally {
			setLoading(false);
		}
	};

	const submitAddInvestment = async () => {
		const startupId = addInvestmentForm.startupId.trim();
		const amount = parseFloat(addInvestmentForm.investmentAmount);
		if (!startupId || isNaN(amount) || amount <= 0) {
			setAlert({ isOpen: true, title: 'Validation', message: 'Enter a valid startup ID and amount.', type: 'info' });
			return;
		}
		setAddInvestmentSubmitting(true);
		try {
			const response = await fetch(getApiUrl('investments/self'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${localStorage.getItem('token')}`
				},
				body: JSON.stringify({
					startupId: parseInt(startupId, 10),
					investmentAmount: amount,
					investmentStage: addInvestmentForm.investmentStage,
					investmentType: addInvestmentForm.investmentType,
					currency: addInvestmentForm.currency,
					investmentDate: addInvestmentForm.investmentDate || new Date().toISOString().split('T')[0],
					notes: addInvestmentForm.notes || undefined
				})
			});
			const data = await response.json().catch(() => ({}));
			if (response.ok) {
				setShowAddInvestmentModal(false);
				setAddInvestmentForm({ startupId: '', investmentAmount: '', investmentStage: 'SEED', investmentType: 'EQUITY', currency: 'INR', investmentDate: new Date().toISOString().split('T')[0], notes: '' });
				loadPortfolio();
				setAlert({ isOpen: true, title: 'Success', message: 'Investment added to your portfolio.', type: 'success' });
			} else {
				setAlert({ isOpen: true, title: 'Error', message: data.message || data.error || 'Failed to add investment.', type: 'error' });
			}
		} catch (error) {
			console.error('Add investment failed:', error);
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to add investment. Please try again.', type: 'error' });
		} finally {
			setAddInvestmentSubmitting(false);
		}
	};

	const loadPortfolio = async () => {
		if (!user?.id) return;
		setLoadingStartups(true);
		try {
			const [portfolioData, investmentsData] = await Promise.all([
				investorAPI.getPortfolio(user.id),
				investorAPI.getInvestments(user.id)
			]);
			setStartups(portfolioData || []);
			// Extract investment details from the getInvestments response
			const investmentData = (investmentsData || []).map(p => ({
				startupId: p.startupId,
				investmentAmount: p.investmentAmount,
				equityPercentage: p.equityPercentage,
				investmentDate: p.investmentDate,
				investmentStage: p.investmentStage,
				status: p.status || 'ACTIVE',
				currency: p.currency || 'INR'
			}));
			setInvestments(investmentData);
		} catch (err) {
			console.error('Failed to load portfolio:', err);
			setStartups([]);
			setInvestments([]);
		} finally {
			setLoadingStartups(false);
		}
	};
	const loadEvents = async () => {
		if (!user?.id) return;
		setLoadingEvents(true);
		try {
			const eventsData = await investorAPI.getEvents(user.id);
			setEvents(eventsData || []);
		} catch (err) {
			console.warn('Events could not be loaded:', err?.message || err);
			setEvents([]);
		} finally {
			setLoadingEvents(false);
		}
	};

	const previewFile = async (file) => {
		try {
			const response = await fileAPI.getFilePreview(file.id);
			window.open(response.previewUrl, '_blank');
		} catch (err) {
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to preview file', type: 'error' });
		}
	};

	const downloadFile = async (file) => {
		try {
			const response = await fileAPI.getFileDownload(file.id);

			// Create a blob from the response data
			const blob = new Blob([response.data], { type: response.headers['content-type'] });

			// Create a link element and trigger the download
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;

			// Try to get filename from content-disposition header
			let filename = file.originalName || 'download';
			const contentDisposition = response.headers['content-disposition'];
			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
				if (filenameMatch && filenameMatch.length === 2) {
					filename = filenameMatch[1];
				}
			}

			link.download = filename;
			document.body.appendChild(link);
			link.click();

			// Clean up
			window.URL.revokeObjectURL(url);
			document.body.removeChild(link);
		} catch (err) {
			console.error('Download error:', err);
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to download file', type: 'error' });
		}
	};

	const handleFileUpload = async (file) => {
		if (!file) return;

		setUploading(true);
		setUploadProgress(0);
		try {
			const formData = new FormData();
			formData.append('file', file);
			if (tags.length > 0) {
				tags.forEach(tag => formData.append('tags', tag));
			}

			await fileAPI.uploadFileForInvestor(formData, (progress) => {
				setUploadProgress(progress);
			});
			setUploadProgress(100);
			// specific delay to show 100% completion
			await new Promise(resolve => setTimeout(resolve, 1000));

			await loadFiles();
			setShowUploadModal(false);
			setSelectedUploadFile(null);
			setTags([]);
		} catch (err) {
			const status = err.response?.status;
			const code = err.response?.data?.code;
			const message = err.response?.data?.message || err.response?.data?.error || 'Failed to upload file';
			if (status === 402 || code === 'INVESTOR_ACTIVITY_REQUIRED') {
				setConfirm({
					isOpen: true,
					title: 'Activity fee required',
					message: 'To add documents you need to pay the one-time activity fee of ₹1,000.',
					type: 'warning',
					confirmText: 'Pay ₹1,000',
					onConfirm: () => {
						setConfirm(c => ({ ...c, isOpen: false }));
						navigate(`/${user?.tenantIdentifier || 'default'}/investor-activity-fee`);
					}
				});
			} else {
				setAlert({ isOpen: true, title: 'Error', message, type: 'error' });
			}
		} finally {
			setUploading(false);
			setUploadProgress(0);
		}
	};

	const handleDrag = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			setSelectedUploadFile(e.dataTransfer.files[0]);
		}
	};

	const addTag = () => {
		if (newTag.trim() && !tags.includes(newTag.trim())) {
			setTags([...tags, newTag.trim()]);
			setNewTag('');
		}
	};

	const removeTag = (tagToRemove) => {
		setTags(tags.filter(tag => tag !== tagToRemove));
	};

	const getFileMetadata = async (file) => {
		try {
			const response = await fileAPI.getFileMetadata(file.id);
			setSelectedFile({ ...file, ...response });
			setShowFileModal(true);
		} catch (err) {
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to get file metadata', type: 'error' });
		}
	};

	const filteredFiles = files.filter(file => {
		const matchesSearch = file.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
			file.uploadedByName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			file.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

		if (!matchesSearch) return false;

		if (fileTab === 'sent') {
			return file.uploadedById === user?.id;
		} else {
			// Received files are those NOT uploaded by me
			return file.uploadedById !== user?.id;
		}
	});

	const totalDocumentPages = Math.max(1, Math.ceil(filteredFiles.length / documentsPageSize));
	const paginatedFiles = filteredFiles.slice((documentsPage - 1) * documentsPageSize, documentsPage * documentsPageSize);

	useEffect(() => {
		setDocumentsPage(1);
	}, [fileTab, searchTerm, documentsPageSize, activeTab]);

	useEffect(() => {
		if (documentsPage > totalDocumentPages) {
			setDocumentsPage(totalDocumentPages);
		}
	}, [documentsPage, totalDocumentPages]);

	const formatDateTime = (dateString) => {
		if (!dateString) return 'N/A';
		return new Date(dateString).toLocaleString('en-IN', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const getFileIcon = (contentType) => {
		if (!contentType) return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';

		if (contentType.includes('pdf')) return 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z';
		if (contentType.includes('word') || contentType.includes('document')) return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
		if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'M9 17v1a1 1 0 001 1h4a1 1 0 001-1v-1m3-2V8a2 2 0 00-2-2H9a2 2 0 00-2 2v7m3-2h6';
		if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'M7 4v16M17 4v16M3 8h4m10 0h4M3 16h4m10 0h4';
		if (contentType.includes('image')) return 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z';
		return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
	};

	const deleteFile = (file) => {
		setConfirm({
			isOpen: true,
			title: 'Delete File',
			message: `Are you sure you want to delete "${file.originalName}"? This will permanently delete the file and remove it from all shared views.`,
			type: 'danger',
			onConfirm: async () => {
				await performDeleteFile(file.id);
			}
		});
	};

	const performDeleteFile = async (fileId) => {
		try {
			await fileAPI.deleteFile(fileId);
			setAlert({ isOpen: true, title: 'Success', message: 'File deleted successfully', type: 'success' });
			loadFiles();
		} catch (err) {
			console.error('Delete error:', err);
			setAlert({ isOpen: true, title: 'Error', message: err.message || 'Failed to delete file', type: 'error' });
		} finally {
			setConfirm({ ...confirm, isOpen: false });
		}
	};

	const formatCurrency = (amount, currencyCode = 'INR') => {
		if (amount === undefined || amount === null) return '-';
		try {
			return new Intl.NumberFormat('en-IN', {
				style: 'currency',
				currency: currencyCode,
				maximumFractionDigits: 0
			}).format(amount);
		} catch (e) {
			return `${currencyCode} ${amount.toLocaleString('en-IN')}`;
		}
	};

	// Calculate stats
	const totalInvestedByCurrency = investments.reduce((acc, inv) => {
		const currency = inv.currency || 'INR';
		if (!acc[currency]) acc[currency] = 0;
		acc[currency] += (inv.investmentAmount || 0);
		return acc;
	}, {});
	const portfolioCount = startups.length;
	const documentsCount = files.length;
	const eventsCount = events.length;

	const getInvestmentForStartup = (startupId) => {
		return investments.find(inv => inv.startupId === startupId) || null;
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
			<main className="min-h-screen flex flex-col">
				{/* Compact Header */}
				<header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10 shrink-0">
					<div className="px-6 py-3">
						<div className="flex items-center justify-between">
							<div>
								<h2 className="text-xl font-bold text-gray-900">
									{activeTab === 'portfolio' && 'My Portfolio'}
									{activeTab === 'documents' && 'Shared Documents'}
									{activeTab === 'events' && 'Events'}
								</h2>
								<p className="text-xs text-gray-500 mt-0.5">
									{activeTab === 'portfolio' && 'Track your investments and portfolio performance'}
									{activeTab === 'documents' && 'Documents shared by your portfolio companies'}
									{activeTab === 'events' && 'Events where your portfolio companies are participating'}
								</p>
							</div>
							{activeTab === 'portfolio' && (
								<button
									type="button"
									onClick={() => setShowAddInvestmentModal(true)}
									className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:shadow-md transition-all flex items-center space-x-2"
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
									</svg>
									<span>Add investment</span>
								</button>
							)}
							{activeTab === 'documents' && (
								<button
									onClick={() => setShowUploadModal(true)}
									className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:shadow-md transition-all flex items-center space-x-2"
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
									</svg>
									<span>Upload</span>
								</button>
							)}
						</div>
					</div>
				</header>

				{/* Stats row + Linked network - compact, interactive */}
				{activeTab === 'portfolio' && (
					<div className="px-6 py-4">
						<div className="flex flex-wrap items-stretch gap-4">
							{/* Clickable stat cards */}
							<button type="button" className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md hover:border-green-200 transition-all text-left flex-1 min-w-[140px] max-w-[200px]">
								<div className="flex items-center justify-between">
									<div>
										<p className="text-xs font-medium text-gray-500">Total Invested</p>
										<div className="mt-0.5">
											{Object.keys(totalInvestedByCurrency).length > 0 ? (
												Object.entries(totalInvestedByCurrency).map(([currency, amount]) => (
													<p key={currency} className="text-lg font-bold text-gray-900">{formatCurrency(amount, currency)}</p>
												))
											) : (
												<p className="text-lg font-bold text-gray-900">{formatCurrency(0)}</p>
											)}
										</div>
									</div>
									<div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
										<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
									</div>
								</div>
								<p className="text-xs text-green-600 font-medium mt-2">Active Investments</p>
							</button>
							<button type="button" className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all text-left flex-1 min-w-[140px] max-w-[200px]">
								<div className="flex items-center justify-between">
									<div>
										<p className="text-xs font-medium text-gray-500">Portfolio</p>
										<p className="text-lg font-bold text-gray-900 mt-0.5">{portfolioCount}</p>
									</div>
									<div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
										<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
									</div>
								</div>
								<p className="text-xs text-gray-500 mt-2">Startups</p>
							</button>
							<button type="button" onClick={() => setActiveTab('documents')} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md hover:border-purple-200 transition-all text-left flex-1 min-w-[140px] max-w-[200px]">
								<div className="flex items-center justify-between">
									<div>
										<p className="text-xs font-medium text-gray-500">Documents</p>
										<p className="text-lg font-bold text-gray-900 mt-0.5">{documentsCount}</p>
									</div>
									<div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-violet-500 rounded-lg flex items-center justify-center flex-shrink-0">
										<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
									</div>
								</div>
								<p className="text-xs text-gray-500 mt-2">View documents →</p>
							</button>
							<button type="button" onClick={() => setActiveTab('events')} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md hover:border-amber-200 transition-all text-left flex-1 min-w-[140px] max-w-[200px]">
								<div className="flex items-center justify-between">
									<div>
										<p className="text-xs font-medium text-gray-500">Events</p>
										<p className="text-lg font-bold text-gray-900 mt-0.5">{eventsCount}</p>
									</div>
									<div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
										<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
									</div>
								</div>
								<p className="text-xs text-gray-500 mt-2">View events →</p>
							</button>
							{/* Linked network card - visible when investor is linked to a network */}
							<div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-100 shadow-sm flex-1 min-w-[200px] max-w-[280px] flex flex-col justify-center">
								<div className="flex items-center justify-between gap-3">
									<div className="min-w-0">
										<p className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Networks</p>
										<p className="text-sm font-semibold text-gray-900 truncate mt-0.5" title={linkedNetwork?.organizationName || linkedNetwork?.name || ''}>
											{linkedNetwork?.organizationName || linkedNetwork?.name || 'View linked networks'}
										</p>
										<p className="text-xs text-gray-500 mt-1">View all networks linked to your investments</p>
									</div>
									<button
										type="button"
										onClick={() => navigate(`/${user?.tenantIdentifier || 'default'}/investor/networks`)}
										className="shrink-0 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"
									>
										View →
									</button>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Tab Content */}
				<div className="px-6 pb-6 flex-1 overflow-auto">
					{/* Portfolio Tab */}
					{activeTab === 'portfolio' && (
						<div className="bg-white rounded-xl border border-gray-100 shadow-sm">
							<div className="p-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
								<div>
									<h3 className="text-base font-semibold text-gray-900">Portfolio Startups</h3>
									<p className="text-xs text-gray-500 mt-0.5">Startups you have invested in</p>
								</div>
								<div className="relative">
									<input
										type="text"
										placeholder="Search portfolio..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="w-56 pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
									/>
									<svg className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
									</svg>
								</div>
							</div>
							<div className="p-4">
								{loadingStartups ? (
									<div className="text-center py-12">
										<div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
											<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
										</div>
										<p className="text-sm font-medium text-gray-900">Loading portfolio...</p>
									</div>
								) : startups.length === 0 ? (
									<div className="text-center py-12">
										<div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
											<svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
											</svg>
										</div>
										<h3 className="text-base font-semibold text-gray-900 mb-1">No portfolio startups yet</h3>
										<p className="text-sm text-gray-500 mb-4">Startups you invest in will appear here</p>
										<button
											type="button"
											onClick={() => setShowAddInvestmentModal(true)}
											className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
										>
											Add investment
										</button>
									</div>
								) : (
									<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
										{startups
											.filter(startup =>
												(startup.organizationName || startup.name || '')
													.toLowerCase()
													.includes(searchTerm.toLowerCase())
											)
											.map((startup) => (
												<StartupPortfolioCard
													key={startup.id}
													startup={startup}
													investment={getInvestmentForStartup(startup.id)}
													showInvestmentDetails={true}
												/>
											))}
									</div>
								)}
							</div>
						</div>
					)}

					{/* Documents Tab - no duplicate header (main header already shows "Shared Documents") */}
					{activeTab === 'documents' && (
						<div className="bg-white rounded-xl border border-gray-100 shadow-sm">
							<div className="p-4 border-b border-gray-100">
								<div className="flex items-center justify-between gap-4 flex-wrap">
									<div className="relative">
										<input
											type="text"
											placeholder="Search documents..."
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
											className="w-56 pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
										/>
										<svg className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
										</svg>
									</div>
								</div>

								{/* Sub-tabs: Received / Sent */}
								<div className="mt-3 border-b border-gray-200">
									<nav className="flex -mb-px space-x-8">
										<button
											onClick={() => setFileTab('received')}
											className={`${fileTab === 'received'
												? 'border-blue-500 text-blue-600'
												: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
												} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
										>
											Received Files
										</button>
										<button
											onClick={() => setFileTab('sent')}
											className={`${fileTab === 'sent'
												? 'border-blue-500 text-blue-600'
												: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
												} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
										>
											Sent Files
										</button>
									</nav>
								</div>
							</div>
							<div className="p-4">
								{loading ? (
									<div className="text-center py-12">
										<div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
											<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
										</div>
										<p className="text-sm font-medium text-gray-900">Loading documents...</p>
									</div>
								) : filteredFiles.length === 0 ? (
									<div className="text-center py-12">
										<div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
											<svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
											</svg>
										</div>
										<h3 className="text-base font-semibold text-gray-900 mb-1">
											{searchTerm
												? 'No documents found'
												: fileTab === 'received'
													? 'No shared documents yet'
													: 'No uploaded documents yet'}
										</h3>
										<p className="text-sm text-gray-500">
											{searchTerm
												? 'Try adjusting your search terms'
												: fileTab === 'received'
													? 'Startups will share documents with you here'
													: 'Documents you upload will appear here'}
										</p>
									</div>
								) : (
									<div className="space-y-3">
										{paginatedFiles.map((file) => (
											<div key={file.id} className="group bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-blue-300 transition-all duration-300">
												<div className="flex items-start justify-between mb-4">
													<div className="flex items-center flex-1 min-w-0">
														<div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mr-3 group-hover:from-blue-200 group-hover:to-indigo-200 transition-colors flex-shrink-0">
															<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={getFileIcon(file.contentType)} />
															</svg>
														</div>
														<div className="flex-1 min-w-0">
															<h3
																className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors cursor-help"
																title={file.originalName}
															>
																{file.originalName}
															</h3>
															{/* Metadata hidden as per request 
															<p className="text-xs text-gray-500">
																{file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
															</p>
															*/}
														</div>
													</div>
												</div>

												{file.tags && file.tags.length > 0 && (
													<div className="flex flex-wrap gap-1 mb-3">
														{file.tags.slice(0, 3).map((tag, index) => (
															<span key={index} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
																{tag}
															</span>
														))}
														{file.tags.length > 3 && (
															<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
																+{file.tags.length - 3}
															</span>
														)}
													</div>
												)}

												<div className="flex items-center justify-between pt-3 border-t border-gray-100">
													<div className="flex items-center text-xs text-gray-500">
														<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
														</svg>
														{formatDateTime(file.uploadedAt)}
													</div>
													<div className="flex space-x-1">
														<button
															onClick={() => previewFile(file)}
															className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all"
															title="Preview"
														>
															<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
															</svg>
														</button>
														<button
															onClick={() => downloadFile(file)}
															className="p-2 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-all"
															title="Download"
														>
															<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
															</svg>
														</button>
														<button
															onClick={() => getFileMetadata(file)}
															className="p-2 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all"
															title="Details"
														>
															<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
															</svg>
														</button>
														{/* Trace Views Button */}
														{(file.uploadedById === user?.id) && (
															<button
																onClick={(e) => {
																	e.stopPropagation();
																	setTraceFile({ id: file.id, name: file.originalName });
																	setShowTraceModal(true);
																}}
																className="p-2 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-all"
																title="Trace Views"
															>
																<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
																</svg>
															</button>
														)}
														{/* Delete button - only for owner */}
														{(file.uploadedById === user?.id) && (
															<button
																onClick={(e) => {
																	e.stopPropagation();
																	deleteFile(file);
																}}
																className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-all"
																title="Delete"
															>
																<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
																</svg>
															</button>
														)}
													</div>
												</div>
											</div>
										))}
										<div className="flex items-center justify-between gap-3 flex-wrap pt-2">
											<p className="text-sm text-gray-600">
												Showing {filteredFiles.length === 0 ? 0 : ((documentsPage - 1) * documentsPageSize + 1)}-
												{Math.min(documentsPage * documentsPageSize, filteredFiles.length)} of {filteredFiles.length}
											</p>
											<div className="flex items-center gap-2">
												<select
													value={documentsPageSize}
													onChange={(e) => setDocumentsPageSize(Number(e.target.value))}
													className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
												>
													<option value={10}>10 / page</option>
													<option value={20}>20 / page</option>
													<option value={50}>50 / page</option>
												</select>
												<button
													onClick={() => setDocumentsPage((p) => Math.max(1, p - 1))}
													disabled={documentsPage <= 1}
													className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
												>
													Prev
												</button>
												<span className="text-sm text-gray-700">Page {documentsPage} / {totalDocumentPages}</span>
												<button
													onClick={() => setDocumentsPage((p) => Math.min(totalDocumentPages, p + 1))}
													disabled={documentsPage >= totalDocumentPages}
													className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
												>
													Next
												</button>
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					)}

					{/* Events Tab - no duplicate header (main header already shows "Events") */}
					{activeTab === 'events' && (
						<div className="bg-white rounded-xl border border-gray-100 shadow-sm">
							<div className="p-4">
								{loadingEvents ? (
									<div className="text-center py-12">
										<div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
											<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
										</div>
										<p className="text-sm font-medium text-gray-900">Loading events...</p>
									</div>
								) : events.length === 0 ? (
									<div className="text-center py-12">
										<div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
											<svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
											</svg>
										</div>
										<h3 className="text-base font-semibold text-gray-900 mb-1">No related events</h3>
										<p className="text-sm text-gray-500">Events where your portfolio startups participate will appear here</p>
									</div>
								) : (
									<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
										{events.map((event) => (
											<div
												key={event.id}
												onClick={() => {
													setSelectedEvent(event);
													setShowEventModal(true);
												}}
												className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:border-blue-300 transition-all duration-300 cursor-pointer"
											>
												{/* Event Header */}
												<div className="h-20 bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 relative">
													<div className="absolute inset-0 bg-black/10"></div>
													<div className="absolute bottom-4 left-4 right-4">
														<h3 className="text-white font-bold truncate text-lg">{event.eventName}</h3>
													</div>
													{event.status && (
														<div className="absolute top-3 right-3">
															<span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${event.status === 'UPCOMING' ? 'bg-green-100/90 text-green-800' :
																event.status === 'ONGOING' ? 'bg-blue-100/90 text-blue-800' :
																	event.status === 'COMPLETED' ? 'bg-gray-100/90 text-gray-800' :
																		'bg-red-100/90 text-red-800'
																}`}>
																{event.status}
															</span>
														</div>
													)}
												</div>

												{/* Event Details */}
												<div className="p-4 space-y-3">
													{event.eventDate && (
														<div className="flex items-center text-sm text-gray-600">
															<svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
															</svg>
															{new Date(event.eventDate).toLocaleDateString('en-IN', {
																year: 'numeric',
																month: 'short',
																day: 'numeric'
															})}
														</div>
													)}
													{event.location && (
														<div className="flex items-center text-sm text-gray-600">
															<svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
															</svg>
															{event.location}
														</div>
													)}
												</div>

												{/* View Indicator */}
												<div className="px-4 pb-4">
													<div className="flex items-center justify-end text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
														<span className="text-sm font-medium">View Details</span>
														<svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
														</svg>
													</div>
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			</main>

			{/* File Details Modal */}
			{showFileModal && selectedFile && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
					<div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
						<div className="p-6 border-b border-gray-100">
							<div className="flex justify-between items-center">
								<h3 className="text-xl font-bold text-gray-900">Document Details</h3>
								<button
									onClick={() => {
										setShowFileModal(false);
										setSelectedFile(null);
									}}
									className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6 space-y-6">
							<div className="flex items-center">
								<div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mr-4">
									<svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={getFileIcon(selectedFile.contentType)} />
									</svg>
								</div>
								<div>
									<h4 className="text-lg font-semibold text-gray-900">{selectedFile.originalName}</h4>
									<p className="text-sm text-gray-500">
										{selectedFile.fileSize ? `${(selectedFile.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
									</p>
								</div>
							</div>

							<div className="space-y-4">
								<div className="bg-gray-50 rounded-xl p-4">
									<p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Uploaded By</p>
									<p className="text-sm font-medium text-gray-900">{selectedFile.uploadedByName}</p>
								</div>
								<div className="bg-gray-50 rounded-xl p-4">
									<p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Upload Date</p>
									<p className="text-sm font-medium text-gray-900">{formatDateTime(selectedFile.uploadedAt)}</p>
								</div>
								<div className="bg-gray-50 rounded-xl p-4">
									<p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">File Type</p>
									<p className="text-sm font-medium text-gray-900">{selectedFile.contentType || 'Unknown'}</p>
								</div>
								{selectedFile.tags && selectedFile.tags.length > 0 && (
									<div className="bg-gray-50 rounded-xl p-4">
										<p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Tags</p>
										<div className="flex flex-wrap gap-2">
											{selectedFile.tags.map((tag, index) => (
												<span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
													{tag}
												</span>
											))}
										</div>
									</div>
								)}
							</div>

							<div className="flex space-x-3 pt-4">
								<button
									onClick={() => {
										setShowFileModal(false);
										setSelectedFile(null);
									}}
									className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
								>
									Close
								</button>
								<button
									onClick={() => {
										previewFile(selectedFile);
										setShowFileModal(false);
									}}
									className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg transition-all"
								>
									Preview
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Upload Modal */}
			{showUploadModal && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
					<div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
						<div className="p-6 border-b border-gray-100">
							<div className="flex justify-between items-center">
								<h3 className="text-xl font-bold text-gray-900">Upload Document</h3>
								<button
									onClick={() => {
										setShowUploadModal(false);
										setSelectedUploadFile(null);
										setTags([]);
										setNewTag('');
									}}
									className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6 space-y-6">
							{/* File Upload Area */}
							<div
								className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${dragActive
									? 'border-blue-500 bg-blue-50'
									: 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
									}`}
								onDragEnter={handleDrag}
								onDragLeave={handleDrag}
								onDragOver={handleDrag}
								onDrop={handleDrop}
							>
								<input
									type="file"
									onChange={(e) => setSelectedUploadFile(e.target.files[0])}
									disabled={uploading}
									className="hidden"
									id="file-input"
								/>
								<label htmlFor="file-input" className="cursor-pointer">
									<div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
										<svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
										</svg>
									</div>
									<p className="text-lg font-semibold text-gray-900 mb-2">
										{selectedUploadFile ? selectedUploadFile.name : 'Drop files here or click to browse'}
									</p>
									<p className="text-sm text-gray-500">PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX up to 100MB</p>
								</label>
							</div>

							{/* Tags */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">Tags (Optional)</label>
								<div className="flex space-x-2 mb-3">
									<input
										type="text"
										value={newTag}
										onChange={(e) => setNewTag(e.target.value)}
										onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
										placeholder="Add a tag..."
										className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
									/>
									<button
										onClick={addTag}
										disabled={!newTag.trim()}
										className="px-4 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
									>
										Add
									</button>
								</div>
								{tags.length > 0 && (
									<div className="flex flex-wrap gap-2">
										{tags.map((tag, index) => (
											<span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
												{tag}
												<button
													onClick={() => removeTag(tag)}
													className="ml-2 text-blue-600 hover:text-blue-800"
												>
													<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
													</svg>
												</button>
											</span>
										))}
									</div>
								)}
							</div>

							{/* Upload Progress */}
							{uploading && (
								<div className="bg-blue-50 rounded-xl p-4">
									<ProgressBar progress={uploadProgress} label="Uploading..." showLabel={true} />
								</div>
							)}

							{/* Action Buttons */}
							<div className="flex space-x-3 pt-4">
								<button
									onClick={() => {
										setShowUploadModal(false);
										setSelectedUploadFile(null);
										setTags([]);
										setNewTag('');
									}}
									disabled={uploading}
									className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors"
								>
									Cancel
								</button>
								<button
									onClick={() => handleFileUpload(selectedUploadFile)}
									disabled={!selectedUploadFile || uploading}
									className="flex-1 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
								>
									{uploading ? 'Uploading...' : 'Upload File'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Event Details Modal */}
			{showEventModal && selectedEvent && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
					<div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
						<div className="p-6 border-b border-gray-100">
							<div className="flex justify-between items-center">
								<div>
									<h3 className="text-xl font-bold text-gray-900">Event Details</h3>
									<p className="text-sm text-gray-500 mt-1">Information about this event</p>
								</div>
								<button
									onClick={() => {
										setShowEventModal(false);
										setSelectedEvent(null);
									}}
									className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6 space-y-6">
							{/* Header Section */}
							<div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-orange-100">
								<div className="flex justify-between items-start mb-4">
									<div>
										<h4 className="text-2xl font-bold text-gray-900">{selectedEvent.eventName}</h4>
										{selectedEvent.eventType && (
											<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white text-orange-800 border border-orange-200 mt-2">
												{selectedEvent.eventType}
											</span>
										)}
									</div>
									<span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold shadow-sm ${selectedEvent.status === 'UPCOMING' ? 'bg-green-100 text-green-800 border border-green-200' :
										selectedEvent.status === 'ONGOING' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
											selectedEvent.status === 'COMPLETED' ? 'bg-gray-100 text-gray-800 border border-gray-200' :
												'bg-red-100 text-red-800 border border-red-200'
										}`}>
										{selectedEvent.status || 'UPCOMING'}
									</span>
								</div>

								<div className="grid grid-cols-2 gap-4 mt-6">
									{selectedEvent.eventDate && (
										<div className="flex items-center text-gray-700 bg-white/60 p-3 rounded-xl">
											<div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3 text-orange-600">
												<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
												</svg>
											</div>
											<div>
												<p className="text-xs font-medium text-gray-500 uppercase">Date & Time</p>
												<p className="font-semibold text-gray-900">
													{new Date(selectedEvent.eventDate).toLocaleString('en-IN', {
														year: 'numeric',
														month: 'short',
														day: 'numeric',
														hour: '2-digit',
														minute: '2-digit'
													})}
												</p>
											</div>
										</div>
									)}
									{selectedEvent.location && (
										<div className="flex items-center text-gray-700 bg-white/60 p-3 rounded-xl">
											<div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3 text-orange-600">
												<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
												</svg>
											</div>
											<div>
												<p className="text-xs font-medium text-gray-500 uppercase">Location</p>
												<p className="font-semibold text-gray-900">{selectedEvent.location}</p>
											</div>
										</div>
									)}
								</div>
							</div>

							{/* Details Section */}
							<div className="space-y-6">
								{selectedEvent.description && (
									<div>
										<h5 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">About The Event</h5>
										<p className="text-gray-600 leading-relaxed whitespace-pre-wrap bg-gray-50 p-4 rounded-xl border border-gray-100">
											{selectedEvent.description}
										</p>
									</div>
								)}

								<div className="grid grid-cols-2 gap-6">
									{selectedEvent.judges && (
										<div>
											<h5 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">Judges</h5>
											<div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
												<p className="text-gray-700 whitespace-pre-wrap">{selectedEvent.judges}</p>
											</div>
										</div>
									)}
									{selectedEvent.sponsors && (
										<div>
											<h5 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">Sponsors</h5>
											<div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
												<p className="text-gray-700 whitespace-pre-wrap">{selectedEvent.sponsors}</p>
											</div>
										</div>
									)}
								</div>

								{(selectedEvent.organizer || selectedEvent.startup) && (
									<div>
										<h5 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Organized By</h5>
										<div className="bg-white border border-gray-200 p-4 rounded-xl flex items-center space-x-4">
											<div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-xl shadow-sm border border-indigo-200">
												{(selectedEvent.organizer?.name || selectedEvent.startup?.name)?.charAt(0)?.toUpperCase()}
											</div>
											<div>
												<p className="font-bold text-gray-900 text-lg">
													{selectedEvent.organizer?.name || selectedEvent.startup?.name}
												</p>
												<p className="text-sm text-gray-500 font-medium">
													{selectedEvent.organizer ? 'Event Organizer' : 'Participating Startup'}
												</p>
											</div>
										</div>
									</div>
								)}
							</div>

							<div className="flex justify-end pt-6 border-t border-gray-100">
								<button
									onClick={() => {
										setShowEventModal(false);
										setSelectedEvent(null);
									}}
									className="px-6 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
								>
									Close
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			<FileTraceModal
				isOpen={showTraceModal}
				onClose={() => {
					setShowTraceModal(false);
					setTraceFile(null);
				}}
				fileId={traceFile?.id}
				fileName={traceFile?.name}
			/>

			{/* Confirm Dialog */}
			<ConfirmDialog
				isOpen={confirm.isOpen}
				onClose={() => setConfirm({ ...confirm, isOpen: false })}
				onConfirm={confirm.onConfirm || (() => { })}
				title={confirm.title}
				message={confirm.message}
				type={confirm.type}
				confirmText={confirm.confirmText || 'Confirm'}
			/>

			{/* Alert Dialog */}
			<AlertDialog
				isOpen={alert.isOpen}
				onClose={() => setAlert({ ...alert, isOpen: false })}
				title={alert.title}
				message={alert.message}
				type={alert.type}
			/>
		</div>
	);
};

export default InvestorDashboard;

